/*
  Keepin' It Halal Brand - US-only cart guard behaviour (Task 1E) and the
  /cart empty-state class reconciler (Task 3).

  WHAT THIS FILE DOES NOT DO
  --------------------------
  It keeps no copy of the blocked state. Whether the cart is blocked, which
  products are restricted, which cart lines they sit on and what the selected
  country is called are all read, at the moment they are needed, from the data
  attributes that snippets/kih-us-guard.liquid renders:

      [data-kih-usguard][data-kih-us-surface="cart"|"drawer"]

  Those elements sit inside `#main-cart-items .js-contents` and inside
  `.drawer__inner`, both of which cart.js and cart-drawer.js already re-render
  from the single /cart/change response, so Liquid recomputes the state on
  every AJAX cart change on both surfaces at no extra request. The persistent
  warning and the accelerated-checkout suppression are likewise Liquid + CSS,
  not JS.

  LISTENERS
  ---------
  Three, all delegated, all attached exactly once:
    - click   on document, capture phase - intercepts the Check out control
              before the form submits, and drives the dialog's own buttons.
    - submit  on document, capture phase - backstop for a form submitted by the
              Check out button through a path other than a click.
    - keydown on window, capture phase - Escape and the dialog's focus trap.
              window is deliberate: capture reaches window before document, so
              stopPropagation() here keeps assets/kih-cart-a11y.js's
              document-level Escape handler from also closing the cart drawer
              behind the dialog.

  No cart request is made here. Removing a restricted line clicks the theme's
  own <cart-remove-button> for that line, which is Dawn's existing cart update
  path, so the Task 4 / Task 5 bidirectional sync refreshes both surfaces -
  one /cart/change per removed line, no reload.

  assets/cart.js, assets/cart-drawer.js and assets/standard-actions-override.js
  are not modified by this feature.
*/
(function () {
  'use strict';

  if (window.kihUsGuardInitialised) return;
  window.kihUsGuardInitialised = true;

  var MODAL_ID = 'KihUsModal';
  var CHECKOUT_SELECTOR = '#checkout, #CartDrawer-Checkout, [name="checkout"]';
  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastOpener = null;
  var lastSurface = 'cart';
  var swallowEscapeKeyup = false;

  /* -------------------------------------------------------------- helpers */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  function modalEl() {
    return document.getElementById(MODAL_ID);
  }

  function dialogEl() {
    var m = modalEl();
    return m ? m.querySelector('.kih-usmodal__dialog') : null;
  }

  function modalIsOpen() {
    var m = modalEl();
    return !!(m && !m.hasAttribute('hidden'));
  }

  /* ------------------------------------------ state, read from the markup */

  function guardEl(surface) {
    var el = null;
    if (surface) {
      el = document.querySelector('[data-kih-usguard][data-kih-us-surface="' + surface + '"]');
    }
    return el || document.querySelector('[data-kih-usguard]');
  }

  function readState(surface) {
    var el = guardEl(surface);
    if (!el) return { blocked: false, names: [], lines: [], count: 0, country: '' };

    var names = (el.getAttribute('data-kih-us-names') || '').split('||').filter(Boolean);
    var lines = (el.getAttribute('data-kih-us-lines') || '')
      .split(',')
      .map(function (value) {
        return parseInt(value, 10);
      })
      .filter(function (value) {
        return value > 0;
      })
      .sort(function (a, b) {
        return a - b;
      });

    return {
      blocked: el.getAttribute('data-kih-us-blocked') === 'true',
      names: names,
      lines: lines,
      count: names.length,
      country: el.getAttribute('data-kih-us-country') || ''
    };
  }

  function surfaceOf(node) {
    return node && node.closest && node.closest('cart-drawer') ? 'drawer' : 'cart';
  }

  function listNames(names) {
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  /* ---------------------------------------------------------------- modal */

  function fill(name, text) {
    var m = modalEl();
    if (!m) return;
    toArray(m.querySelectorAll('[data-kih-us-fill="' + name + '"]')).forEach(function (el) {
      el.textContent = text;
    });
  }

  function setBusy(busy) {
    var m = modalEl();
    if (!m) return;
    m.classList.toggle('kih-usmodal--busy', !!busy);
    var d = dialogEl();
    if (d) d.setAttribute('aria-busy', busy ? 'true' : 'false');
    toArray(m.querySelectorAll('button[data-kih-us-action]')).forEach(function (button) {
      button.disabled = !!busy;
    });
  }

  function openModal(state, opener) {
    var m = modalEl();
    if (!m || modalIsOpen()) return;

    lastOpener = opener || null;
    lastSurface = surfaceOf(opener);

    fill('names', listNames(state.names));
    fill('country', state.country);
    fill('word', state.count > 1 ? 'items' : 'item');

    setBusy(false);
    m.removeAttribute('hidden');
    document.documentElement.classList.add('kih-usmodal-is-open');

    var d = dialogEl();
    if (d) d.focus();
  }

  function focusableItems() {
    var d = dialogEl();
    if (!d) return [];
    return toArray(d.querySelectorAll(FOCUSABLE)).filter(isVisible);
  }

  function restoreFocus() {
    if (lastOpener && document.contains(lastOpener) && isVisible(lastOpener) && !lastOpener.disabled) {
      lastOpener.focus();
      return;
    }
    var fallback =
      toArray(document.querySelectorAll(CHECKOUT_SELECTOR)).filter(isVisible)[0] ||
      document.getElementById('cart-icon-bubble');
    if (fallback && typeof fallback.focus === 'function') fallback.focus();
  }

  function closeModal(returnFocus) {
    var m = modalEl();
    if (!m || !modalIsOpen()) return;
    m.setAttribute('hidden', '');
    document.documentElement.classList.remove('kih-usmodal-is-open');
    setBusy(false);
    if (returnFocus !== false) restoreFocus();
    lastOpener = null;
  }

  /* ------------------------------------------------------- remove restricted */

  // Resolves once the theme has finished a cart update. cart.js publishes
  // cartUpdate *after* it has replaced every rendered section synchronously,
  // so by the time this fires the guard attributes are already the new ones.
  function onceCartUpdate(callback) {
    var done = false;
    var unsubscribe = null;

    function finish() {
      if (done) return;
      done = true;
      if (unsubscribe) unsubscribe();
      callback();
    }

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, finish);
    }
    window.setTimeout(finish, 6000);
  }

  function removeControl(surface, line) {
    var row = document.getElementById(
      (surface === 'drawer' ? 'CartDrawer-Item-' : 'CartItem-') + line
    );
    return row ? row.querySelector('cart-remove-button') : null;
  }

  // One request per restricted line. Lines are removed highest index first:
  // removing line N never shifts the index of any line below it, so the
  // remaining indexes stay valid. The list is re-read from the freshly
  // rendered guard between removals anyway.
  function removeRestricted() {
    var surface = lastSurface;
    var attempts = 0;
    setBusy(true);

    function step() {
      attempts += 1;
      var state = readState(surface);
      if (!state.blocked || state.lines.length === 0 || attempts > 25) {
        setBusy(false);
        closeModal();
        return;
      }

      var line = state.lines[state.lines.length - 1];
      var control =
        removeControl(surface, line) || removeControl(surface === 'drawer' ? 'cart' : 'drawer', line);
      if (!control) {
        setBusy(false);
        closeModal();
        return;
      }

      onceCartUpdate(function () {
        window.setTimeout(step, 0);
      });
      control.click();
    }

    step();
  }

  /* ------------------------------------------------- change country/region */

  function countryButtons() {
    return toArray(document.querySelectorAll('form[id*="Country"] .localization-form__select'));
  }

  function revealSelector(button) {
    try {
      button.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch (error) {
      button.scrollIntoView();
    }
    button.focus();
    if (button.getAttribute('aria-expanded') !== 'true') button.click();
  }

  // Focuses and opens the localization country selector the theme already
  // renders (header on desktop, header menu drawer on small screens). No
  // second selector is built here.
  function openCountrySelector() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') {
      drawer.close();
    }

    var button = countryButtons().filter(isVisible)[0];
    if (button) {
      revealSelector(button);
      return;
    }

    var menuToggle = document.querySelector('header-drawer summary, .header__icon--menu');
    if (menuToggle) menuToggle.click();
    window.setTimeout(function () {
      var mobileButton = countryButtons().filter(isVisible)[0];
      if (mobileButton) revealSelector(mobileButton);
    }, 350);
  }

  /* ------------------------------------------------------------- listeners */

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var action = target.closest('[data-kih-us-action]');
      if (action && modalIsOpen() && modalEl().contains(action)) {
        event.preventDefault();
        event.stopPropagation();
        var name = action.getAttribute('data-kih-us-action');
        if (name === 'close') closeModal();
        else if (name === 'country') {
          closeModal(false);
          openCountrySelector();
        } else if (name === 'remove') removeRestricted();
        return;
      }

      var checkout = target.closest(CHECKOUT_SELECTOR);
      if (!checkout) return;

      var surface = surfaceOf(checkout);
      var state = readState(surface);
      if (!state.blocked) return;

      event.preventDefault();
      event.stopPropagation();
      openModal(state, checkout);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || (form.id !== 'cart' && form.id !== 'CartDrawer-Form')) return;
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;

      var surface = form.id === 'CartDrawer-Form' ? 'drawer' : 'cart';
      var state = readState(surface);
      if (!state.blocked) return;

      event.preventDefault();
      event.stopPropagation();
      openModal(state, submitter);
    },
    true
  );

  window.addEventListener(
    'keydown',
    function (event) {
      if (!modalIsOpen()) return;

      var key = event.key;
      if (key === 'Escape' || key === 'Esc') {
        event.preventDefault();
        // Stops kih-cart-a11y.js's document-level Escape handler, so Escape
        // closes this dialog without also closing the cart drawer behind it.
        event.stopPropagation();
        // <cart-drawer> closes itself on keyUP. Closing the dialog puts focus
        // back on #CartDrawer-Checkout, which is inside the drawer, so the
        // keyup half of this same press would bubble there and close the
        // drawer too. Swallow that one keyup.
        swallowEscapeKeyup = true;
        closeModal();
        return;
      }

      if (key !== 'Tab') return;

      var items = focusableItems();
      var dialog = dialogEl();
      if (!dialog || items.length === 0) {
        event.preventDefault();
        return;
      }

      // Dawn's global trapFocus also binds keydown on document; stopping
      // propagation here keeps the two traps from fighting while this dialog
      // is the top layer.
      event.stopPropagation();

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (!dialog.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    true
  );

  /* ------------------------------------------------------------------------
     Task 3 - keep the /cart empty-state class honest.

     `.is-empty` on <cart-items> and on #main-cart-footer is what decides
     whether /cart shows the empty-state H1 ("Your cart is empty") or the
     populated-cart H1 ("Your cart"). cart.js toggles it on its own
     /cart/change path, and cart-drawer.js's entries keep the drawer path
     covered, but the external refresh path (Standard Actions / an app / the
     Shop app, via standard-actions-override.js -> CartItems#onCartUpdate)
     only replaces <cart-items>'s innerHTML - it never touches the class. An
     empty -> populated transition arriving that way therefore left /cart
     showing the empty-state heading over a cart that had items.

     This observer derives the class from the markup that was just rendered
     (a line item is present or it is not), so it agrees with cart.js on every
     path it already handles and is a no-op there. It is additive: no cart
     synchronisation code is changed.
  ------------------------------------------------------------------------ */

  function reconcileEmptyState() {
    var cartItems = document.querySelector('cart-items');
    if (!cartItems) return;
    var hasLines = !!cartItems.querySelector('.cart-item');
    cartItems.classList.toggle('is-empty', !hasLines);
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', !hasLines);
  }

  function watchEmptyState() {
    var cartItems = document.querySelector('cart-items');
    if (!cartItems) return;
    reconcileEmptyState();
    if (!window.MutationObserver) return;
    // childList/subtree only: the class toggles above are attribute changes,
    // so this cannot re-trigger itself.
    new window.MutationObserver(reconcileEmptyState).observe(cartItems, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener(
    'keyup',
    function (event) {
      if (!swallowEscapeKeyup) return;
      swallowEscapeKeyup = false;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  /* --------------------------------------------- keep an open dialog honest

     If the cart changes while the dialog is open - the shopper removed the
     restricted line in another tab, or the drawer and the page synced - the
     dialog must not keep asserting a block that no longer exists. This reads
     the freshly rendered guard again and either refreshes the interpolated
     copy or closes the dialog. It still holds no state of its own.
  --------------------------------------------------------------------------- */

  function resyncOpenModal() {
    if (!modalIsOpen()) return;
    var state = readState(lastSurface);
    if (!state.blocked) {
      closeModal();
      return;
    }
    fill('names', listNames(state.names));
    fill('country', state.country);
    fill('word', state.count > 1 ? 'items' : 'item');
  }

  function bindCartUpdate() {
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS ||
      !PUB_SUB_EVENTS.cartUpdate
    ) {
      return false;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      window.setTimeout(resyncOpenModal, 0);
    });
    return true;
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    watchEmptyState();
    if (!bindCartUpdate()) window.addEventListener('load', bindCartUpdate, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
